// Reporting money formatter — native-currency DOLLARS (NOT cents).
//
// The reporting fact table stores `spend` as NUMERIC in the ad account's native
// currency (dollars), NOT integer cents — so money.ts:centsToDisplay (which
// divides by 100) is WRONG here. This formats a dollar amount with the account's
// currency marker, never converting (spend is shown per-currency; see §6).

import type { CurrencyAmount } from '../types/reporting.js';
import type { FxRateSnapshot } from '../types/fxRates.js';
import { toDisplayCurrency } from './reporting/currencyConversion.js';
import { CURRENCY_SYMBOL } from './money.js';

function symbolFor(currency: string): string {
  return (CURRENCY_SYMBOL as Record<string, string>)[currency] ?? '$';
}

// Format one native-currency amount, e.g. 12340.5 + 'AUD' → "A$12,340.50".
// `dp` = decimal places (2 for money; 2 for CPL/cost-per-lead ratios too).
export function formatReportingAmount(amount: number, currency: string, dp = 2): string {
  const n = Number.isFinite(amount) ? amount : 0;
  const withSep = n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return `${symbolFor(currency)}${withSep}`;
}

// Format a per-currency money stat as stacked subtotals with markers, e.g.
// [{AUD,12340},{NZD,6780}] → "A$12,340.00 · NZ$6,780.00". Never sums across
// currencies. Empty → "—". Sorted by currency for stable display.
export function formatCurrencyAmounts(amounts: CurrencyAmount[], dp = 2): string {
  if (amounts.length === 0) return '—';
  return [...amounts]
    .sort((a, b) => a.currency.localeCompare(b.currency))
    .map((a) => formatReportingAmount(a.amount, a.currency, dp))
    .join(' · ');
}

/**
 * The ONE money renderer both reporting dashboards use once a DISPLAY CURRENCY
 * may be active. It is the single seam where the agency's blended view is
 * produced, which is why every money surface routes through it rather than
 * calling `formatCurrencyAmounts` directly.
 *
 * Native mode (the default) is byte-for-byte the old behaviour: per-currency,
 * never summed. In display-currency mode the amounts are converted through the
 * rate snapshot and shown as ONE figure.
 *
 * ⚠️ An amount with no usable rate is NOT folded in and NOT dropped — it is
 * appended natively and marked, e.g. "A$12,340.00 · £500.00 (no rate)". Folding
 * it in would be the raw blend; dropping it would be a silent shortfall the
 * reader cannot see. When NOTHING could be converted this degrades all the way
 * back to the native rendering rather than showing a misleading "0".
 */
export function formatDisplayMoney(
  amounts: CurrencyAmount[],
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
  dp = 2,
): string {
  const converted = toDisplayCurrency(amounts, display, snapshot);
  if (!converted) return formatCurrencyAmounts(amounts, dp);
  if (amounts.length > 0 && converted.unconverted.length === amounts.length) {
    return formatCurrencyAmounts(amounts, dp);
  }
  if (amounts.length === 0) return '—';
  const head = formatReportingAmount(converted.amount, converted.currency, dp);
  if (converted.unconverted.length === 0) return head;
  return `${head} · ${formatCurrencyAmounts(converted.unconverted, dp)} (no rate)`;
}
